
import React from 'react';
import { ExternalLink, AlertTriangle } from 'lucide-react';

// Mock specifications that would typically come from product data
const generateMockSpecs = (product) => {
  const baseSpecs = {
    'General Information': {
      'Brand': product?.brand || 'Various',
      'ASIN': product?.asin || 'N/A',
      'Item Weight': 'See Amazon for details',
      'Product Dimensions': 'See Amazon for details',
      'Item Model Number': 'See Amazon for details'
    },
    'Technical Details': {
      'Manufacturer': product?.brand || 'Various',
      'Release Date': product?.release_date || 'See Amazon for details',
      'Warranty': product?.warranty || 'See Amazon for details',
      'Customer Reviews': `${product?.rating || 'N/A'} out of 5 stars`
    }
  };

  // Add category-specific specs based on product features
  if (product?.features?.some(f => f.toLowerCase().includes('wireless'))) {
    baseSpecs['Connectivity'] = {
      'Wireless Technology': 'See Amazon for details',
      'Bluetooth': 'See Amazon for details',
      'Wi-Fi': 'See Amazon for details'
    };
  }

  if (product?.features?.some(f => f.toLowerCase().includes('battery'))) {
    baseSpecs['Power'] = {
      'Battery Life': 'See Amazon for details',
      'Charging': 'See Amazon for details',
      'Power Source': 'See Amazon for details'
    };
  }

  return baseSpecs;
};

export function ProductSpecs({ product }) {
  const specs = generateMockSpecs(product);
  // Ensure partner tag is always available with fallback
  const partnerTag = import.meta.env.VITE_AMAZON_PARTNER_TAG || 'dtoplist-20';
  const amazonUrl = `https://www.amazon.com/dp/${product?.asin}?tag=${partnerTag}`;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Technical Specifications</h3>
        <a
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm transition-colors"
        >
          <ExternalLink size={14} />
          View complete specs on Amazon
        </a>
      </div>

      {/* Policy Compliance Warning */}
      <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
          <div>
            <h4 className="font-medium text-amber-800 dark:text-amber-200 mb-1">
              Specification Disclaimer
            </h4>
            <p className="text-amber-700 dark:text-amber-300 text-sm">
              Product specifications may change without notice. For the most accurate and up-to-date 
              technical specifications, please refer to the official Amazon product page.
            </p>
          </div>
        </div>
      </div>
      
      {Object.entries(specs).map(([category, categorySpecs]) => (
        <div key={category} className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
          <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3">{category}</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {Object.entries(categorySpecs).map(([key, value]) => (
              <div key={key} className="flex justify-between py-1 border-b border-gray-200 dark:border-gray-600 last:border-0">
                <span className="text-gray-600 dark:text-gray-400 font-medium">{key}:</span>
                <span className="text-gray-900 dark:text-gray-100 text-right">{value}</span>
              </div>
            ))}
          </div>
        </div>
      ))}
      
      {/* What's in the Box - Policy Compliant */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Package Contents</h4>
        <p className="text-blue-800 dark:text-blue-200 text-sm mb-3">
          Package contents may vary. Please check the Amazon product page for exact items included.
        </p>
        <div className="text-blue-800 dark:text-blue-200 space-y-1 text-sm">
          <p>• Main product item</p>
          <p>• Accessories (varies by product)</p>
          <p>• Documentation (varies by product)</p>
          <p>• Warranty information (if applicable)</p>
        </div>
      </div>

      {/* Call to Action */}
      <div className="text-center py-6 border-t border-gray-200 dark:border-gray-700">
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Need detailed specifications or technical support?
        </p>
        <a
          href={amazonUrl}
          target="_blank"
          rel="noopener noreferrer nofollow sponsored"
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <ExternalLink size={16} />
          View Full Details on Amazon
        </a>
      </div>
    </div>
  );
}
